import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query'
import { Check, Plus, Search, Wand2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { actionsQuery } from '@/api/actions'
import { catalogPuppetsInfiniteQuery, objectSearchQuery, type CatalogPuppet } from '@/api/puppets'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/components/ui/utils'
import { PuppetThumb } from '@/features/puppets/PuppetCard'

import { gerund, knownMoveWords, newMoves } from './moves'
import { bestThing, candidateNouns, MAX_LOOKUPS, MAX_THINGS, singular, thingFromCatalog, THINGS_DEBOUNCE_MS, type ThingPick } from './things'

/**
 * Step 3's "Things in the story" and "Living background" (docs/SKEL-STORY.md §6 Q10): the objects
 * the idea mentions, found as the parent types (see `things.ts`), which the parent can remove or add
 * to; and the switch for the scenery puppets that move behind the actors (Q7).
 *
 * "Three apples" are three things: the chip says ×3, and the count of things counts every copy, the
 * way the story job reads the idea (`inventory.count_before` / `copies_of` in the backend).
 */

/* -------------------------------------------------------------------------- */
/* How many of a thing                                                         */
/* -------------------------------------------------------------------------- */

/** A thing of the idea with how many of it the idea asks for (`count`, 1 when absent). */
export interface CountedThing extends ThingPick {
  count?: number
}

/** The most copies one named thing gets (backend `inventory.MAX_COPIES`). */
export const MAX_COPIES = 3

/** Number words before a thing (backend `inventory.COUNT_WORDS`). */
const COUNT_WORDS: Readonly<Record<string, number>> = {
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  both: 2,
  pair: 2,
  couple: 2,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
}

/** Words that may stand between a number and its thing (backend `inventory.DESCRIBING_WORDS`). */
const DESCRIBING = new Set(
  `little big small tiny huge giant large round shiny bright soft juicy yummy tasty sweet fresh ripe fluffy
  sparkly magic special favourite favorite lovely pretty beautiful new old red blue green yellow orange purple
  pink brown black white golden silver gold striped spotty wooden`.split(/\s+/),
)
/** Describing words skipped at most between the number and the thing. */
const MAX_DESCRIBING = 3

/**
 * How many of the thing `word` (singular, as `candidateNouns` found it) the idea asks for: a number
 * word right before its first mention, or before the few words that describe it ("three big red
 * apples", "a pair of socks"); anything else in between means one. At most `MAX_COPIES`.
 */
export function ideaCount(idea: string, word: string | null): number {
  if (!word) return 1
  const raw = idea.toLowerCase().match(/[a-z]+|[0-9]+/g) ?? []
  const at = raw.findIndex((w) => singular(w) === word)
  if (at < 0) return 1
  let j = at - 1
  if (j >= 1 && raw[j] === 'of' && (raw[j - 1] === 'pair' || raw[j - 1] === 'couple')) return 2
  let skipped = 0
  while (j >= 0 && DESCRIBING.has(raw[j] ?? '') && skipped < MAX_DESCRIBING) {
    j -= 1
    skipped += 1
  }
  const n = j >= 0 ? COUNT_WORDS[raw[j] ?? ''] : undefined
  return n ? Math.min(MAX_COPIES, n) : 1
}

/** How many copies of `thing` the story gets (1 for a thing without a count). */
export function copiesOf(thing: ThingPick): number {
  const n = 'count' in thing && typeof thing.count === 'number' ? thing.count : 1
  return Math.max(1, Math.min(MAX_COPIES, Math.floor(n)))
}

/**
 * Copies per thing as the story will have them: each thing its count, while the story has room
 * (`max`); a thing past the room still comes once (backend `inventory.build`).
 */
export function storyCopies(things: readonly ThingPick[], max: number = MAX_THINGS): number[] {
  let used = 0
  return things.map((t) => {
    const n = Math.max(1, Math.min(copiesOf(t), max - used))
    used += n
    return n
  })
}

/* -------------------------------------------------------------------------- */
/* Finding things in the idea                                                  */
/* -------------------------------------------------------------------------- */

export interface IdeaThings {
  /** The best catalog object for each thing-word of the idea, in the idea's order, with its count. */
  found: CountedThing[]
  /** `found` answers the idea as it is now (typing paused, every lookup answered). */
  settled: boolean
  /** A lookup is on the wire. */
  looking: boolean
}

export function useIdeaThings(idea: string, castNames: readonly string[]): IdeaThings {
  const now = idea.trim()
  const text = useDebounced(now, THINGS_DEBOUNCE_MS)
  const namesKey = castNames.join('\n')
  const candidates = useMemo(() => candidateNouns(text, namesKey ? namesKey.split('\n') : []).slice(0, MAX_LOOKUPS), [text, namesKey])
  const lookups = useQueries({ queries: candidates.map((c) => objectSearchQuery(c.noun)) })

  const found: CountedThing[] = []
  candidates.forEach((c, i) => {
    const items = lookups[i]?.data?.items
    const best = items ? bestThing(c, items) : null
    if (best && !found.some((t) => t.uid === best.asset_uid)) found.push({ ...thingFromCatalog(best, c.word), count: ideaCount(text, c.word) })
  })
  return { found, settled: text === now && lookups.every((l) => l.isSuccess), looking: lookups.some((l) => l.isFetching) }
}

function useDebounced<T>(value: T, ms: number): T {
  const [out, setOut] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setOut(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return out
}

/* -------------------------------------------------------------------------- */
/* Things in the story                                                         */
/* -------------------------------------------------------------------------- */

export interface ThingsFieldProps {
  things: ThingPick[]
  looking?: boolean
  onRemove(thing: ThingPick): void
  onAdd(thing: ThingPick): void
  max?: number
}

export function ThingsField({ things, looking = false, onRemove, onAdd, max = MAX_THINGS }: ThingsFieldProps) {
  const [adding, setAdding] = useState(false)
  const copies = storyCopies(things, max)
  const total = copies.reduce((a, b) => a + b, 0)
  const full = total >= max
  return (
    <div className="flex flex-col gap-2" data-testid="story-things">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span id="things-label" className="text-sm font-medium text-ink">
          Things in the story
        </span>
        <span className="text-xs tabular-nums text-ink-3" aria-live="polite">
          {looking ? 'Looking at your idea…' : total > 0 ? `${total} of ${max}` : ''}
        </span>
      </div>
      <ul className="flex flex-wrap items-center gap-2" aria-labelledby="things-label">
        {things.map((t, i) => {
          const n = copies[i] ?? 1
          const label = n > 1 ? `${t.name} ×${n}` : t.name
          return (
            <li key={t.uid}>
              <span
                className="inline-flex h-11 max-w-full items-center gap-2 rounded-full bg-accent-soft pl-1 pr-1.5 text-sm font-medium text-ink"
                title={t.learning ? `${label} learns to move while the story is written` : label}
                data-uid={t.uid}
                data-count={n}
              >
                <span className="size-9 shrink-0 overflow-hidden rounded-full bg-paper">{t.thumb ? <img src={t.thumb} alt="" className="size-full object-contain" /> : null}</span>
                <span className="max-w-40 truncate">{t.name}</span>
                {n > 1 ? (
                  <span className="-ml-1 rounded-full bg-paper px-1.5 text-xs font-semibold tabular-nums text-ink-2" data-testid="thing-count">
                    <span aria-hidden>×{n}</span>
                    <span className="sr-only">, {n} of them</span>
                  </span>
                ) : null}
                <button type="button" onClick={() => onRemove(t)} aria-label={n > 1 ? `Remove ${t.name} (all ${n})` : `Remove ${t.name}`} className="inline-flex size-8 shrink-0 items-center justify-center rounded-full hover:bg-paper/70 focus-visible:focus-ring">
                  <X className="size-4" aria-hidden />
                </button>
              </span>
            </li>
          )
        })}
        <li>
          <Button variant="outline" className="rounded-full" onClick={() => setAdding(true)} disabled={full} title={full ? `Up to ${max} things` : undefined}>
            <Plus aria-hidden />
            Add a thing
          </Button>
        </li>
      </ul>
      <p className="text-xs text-ink-2">
        {things.length > 0 ? 'Your friends will use these in the story. Tap × to leave one out.' : 'Name a thing in your idea — a ball, a kite, a cake — and it shows up here.'}
      </p>
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent size="lg">
          <AddThing
            picked={new Set(things.map((t) => t.uid))}
            onPick={(t) => {
              onAdd(t)
              setAdding(false)
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** The objects tab of the catalog, searchable. Mounted only while the dialog is open. */
function AddThing({ picked, onPick }: { picked: ReadonlySet<string>; onPick(thing: ThingPick): void }) {
  const [q, setQ] = useState('')
  const needle = useDebounced(q.trim(), 250)
  const list = useInfiniteQuery(catalogPuppetsInfiniteQuery(['object'], needle, 30))
  const items = useMemo(() => {
    const seen = new Set<string>()
    const out: CatalogPuppet[] = []
    for (const page of list.data?.pages ?? []) {
      for (const c of page.items) {
        if (seen.has(c.asset_uid)) continue
        seen.add(c.asset_uid)
        out.push(c)
      }
    }
    return out
  }, [list.data])

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add a thing</DialogTitle>
        <DialogDescription>Something your friends can hold, share, ride, eat or play with.</DialogDescription>
      </DialogHeader>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="A ball, a kite, a cake…" aria-label="Search for a thing" className="pl-9" autoFocus />
      </div>
      {list.isPending ? (
        <p className="text-sm text-ink-3">Looking for things…</p>
      ) : list.isError ? (
        <p className="text-sm text-danger" role="alert">
          The things could not be loaded.{' '}
          <button type="button" className="underline" onClick={() => void list.refetch()}>
            Try again
          </button>
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-3">{needle ? `Nothing called “${needle}”.` : 'No things yet.'}</p>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(6.5rem,1fr))] gap-2" data-testid="thing-catalog">
          {items.map((c) => {
            const on = picked.has(c.asset_uid)
            return (
              <li key={c.asset_uid}>
                <button
                  type="button"
                  aria-pressed={on}
                  aria-label={c.name}
                  onClick={() => onPick(thingFromCatalog(c))}
                  className={cn(
                    'relative flex w-full flex-col overflow-hidden rounded-xl border bg-paper text-left transition-shadow hover:shadow-pop focus-visible:focus-ring',
                    on ? 'border-accent ring-2 ring-accent' : 'border-line',
                  )}
                >
                  <PuppetThumb src={c.thumb_url} name={c.name} />
                  {on ? (
                    <span className="absolute right-1.5 top-1.5 inline-flex size-6 items-center justify-center rounded-full bg-accent text-accent-ink" aria-hidden>
                      <Check className="size-3.5" />
                    </span>
                  ) : null}
                  <span className="truncate px-2 py-1.5 text-xs font-semibold text-ink">{c.name}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {list.hasNextPage ? (
        <Button variant="secondary" className="self-center justify-self-center" onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>
          Show more
        </Button>
      ) : null}
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* New moves (Part 3 R7)                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The idea's verbs no action of the registry covers (see `moves.ts`), once typing pauses and the
 * action list is here; nothing while either is missing (no hint beats a wrong one).
 */
export function useNewMoves(idea: string, castNames: readonly string[], enabled = true): string[] {
  const text = useDebounced(idea.trim(), THINGS_DEBOUNCE_MS)
  const actions = useQuery({ ...actionsQuery(), staleTime: 60_000, enabled })
  const namesKey = castNames.join('\n')
  return useMemo(() => {
    if (!actions.data || actions.data.length === 0 || text.length < 3) return []
    return newMoves(text, namesKey ? namesKey.split('\n') : [], knownMoveWords(actions.data))
  }, [actions.data, text, namesKey])
}

/** "New moves the studio will learn: juggling" — under "Things in the story". */
export function NewMovesHint({ moves }: { moves: readonly string[] }) {
  if (moves.length === 0) return null
  return (
    <p className="-mt-2 flex items-start gap-2 text-sm text-ink-2" data-testid="new-moves" aria-live="polite">
      <Wand2 className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
      <span>
        New moves the studio will learn: <span className="font-medium text-ink">{moves.map(gerund).join(', ')}</span>
      </span>
    </p>
  )
}

/* -------------------------------------------------------------------------- */
/* Living background                                                           */
/* -------------------------------------------------------------------------- */

export function SceneryToggle({ checked, onChange }: { checked: boolean; onChange(on: boolean): void }) {
  return (
    <label htmlFor="story-scenery" className="flex cursor-pointer items-start gap-3 rounded-xl border border-line p-3 hover:bg-paper-2">
      <button
        id="story-scenery"
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby="story-scenery-label"
        aria-describedby="story-scenery-hint"
        onClick={() => onChange(!checked)}
        className={cn('relative mt-0.5 inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus-visible:focus-ring', checked ? 'bg-accent' : 'bg-line-strong')}
      >
        <span className={cn('inline-block size-5 rounded-full bg-white shadow-card transition-transform', checked ? 'translate-x-6' : 'translate-x-1')} aria-hidden />
      </button>
      <span className="flex min-w-0 flex-col">
        <span id="story-scenery-label" className="text-sm font-medium text-ink">
          Living background
        </span>
        <span id="story-scenery-hint" className="text-xs text-ink-2">
          Trees sway, clouds drift and little creatures potter about behind your friends.
        </span>
      </span>
    </label>
  )
}
